import React from 'react';
import { FormattedMessage, MessageDescriptor, defineMessages } from 'react-intl';
import { IContactInfo } from '../../entities/company';
import EmailIcon from '../icons/Email';
import Location from '../icons/Location';
import PhoneIcon from '../icons/Phone';
import Nl2br from '../Nl2br/Nl2br';
import Facebook from '../icons/Facebook';
import globalMessages from 'src/services/globalMessages';
import WebSite from '../icons/WebSite';
import { useLayoutEffect, useRef } from 'react';
import { useWindowWidth } from 'src/hooks';
import { isMessageDescriptor } from 'src/components/form-components/helpers';

const messages = defineMessages({
    webpage: {
        id: 'webpage',
        defaultMessage: 'Weboldal'
    },
    facebookPage: {
        id: 'facebookPage',
        defaultMessage: 'Facebook oldal'
    }
});

export const enum ContactDetailType {
    Phone = 'tel:',
    Email = 'mailto:',
    External = ''
}

interface IContactDetailProps {
    icon: JSX.Element,
    text: string | MessageDescriptor,
    type: ContactDetailType,
    link: string | null
}

const ContactDetail: React.FunctionComponent<IContactDetailProps> = ({ icon, text, type, link }) => {
    const createRealLink = () => {
        if (!link) {
            return undefined;
        }

        if (type === ContactDetailType.External
            && !(link.startsWith('http://') || link.startsWith('https://'))) {
            return '//' + link;
        }

        return type + link;
    }

    const linkAttributes = {
        href: createRealLink(),
        className: 'contact',
        target: type === ContactDetailType.External && link ? '_blank' : '_self',
        rel: 'noopener noreferrer'
    }

    return (
        <div className="contacts__item">
            <a {...linkAttributes}>
                <div className="contact__icon">
                    {icon}
                </div>
                {isMessageDescriptor(text)
                    ? <FormattedMessage {...text} />
                    : <Nl2br {...{ text }} />
                }
            </a>
        </div>
    );
}

const ContactInfo = (contactInfo: IContactInfo) => {

    const { PublicEmail, PublicPhoneNumber, Address, MapLink, OpeningHours, WebPageLink, FacebookPageLink } = contactInfo;
    const hasDetails = PublicEmail || PublicPhoneNumber || Address || MapLink;
    const hasSocial = WebPageLink || FacebookPageLink;

    const ref = useRef<HTMLElement>(null);

    const Details = () =>
        hasDetails ?
            <section ref={ref} className="contacts">
                {PublicEmail && <ContactDetail text={PublicEmail} icon={<EmailIcon />} type={ContactDetailType.Email} link={PublicEmail} />}
                {Address && <ContactDetail text={Address} icon={<Location />} type={ContactDetailType.External} link={MapLink} />}
                {PublicPhoneNumber && <ContactDetail text={PublicPhoneNumber} icon={<PhoneIcon />} type={ContactDetailType.Phone} link={PublicPhoneNumber} />}
            </section> : null

    const Hours = () => OpeningHours ?
        <section className="contact-info__opening-hours">
            {OpeningHours}
        </section> : null

    const Social = () => hasSocial ?
        <section className="contacts">
            {WebPageLink && <ContactDetail text={messages.webpage} icon={<WebSite />} type={ContactDetailType.External} link={WebPageLink} />}
            {FacebookPageLink && <ContactDetail text={messages.facebookPage} icon={<Facebook />} type={ContactDetailType.External} link={FacebookPageLink} />}
        </section > : null

    const windowWidth = useWindowWidth();

    useLayoutEffect(() => {

        if (!ref.current) {
            return;
        }

        const contactsSection = ref.current;
        contactsSection.classList.remove('contacts--vertical');

        if (contactsSection.children.length > 1) {
            const hasOverlappingElements =
                twoCombinations(
                    Array.from(contactsSection.children,
                        c => c.children[0].getBoundingClientRect())
                ).some(([rect1, rect2]) =>
                    rect1.right > rect2.left)

            if (hasOverlappingElements) {
                contactsSection.classList.add('contacts--vertical');
            }
        }
    }, [windowWidth, ref.current]);

    return (
        hasDetails || OpeningHours || hasSocial ?
            <address className="contact-info">
                <h1 className="contact-info__title">
                    <FormattedMessage {...globalMessages.contactUs} />
                </h1>
                <Details />
                <Hours />
                <Social />
            </address>
            : null
    );
}

const twoCombinations = <T,>(arr: T[]): Array<[T, T]> => {
    return (arr.length === 3
        ? [
            [arr[0], arr[1]],
            [arr[0], arr[2]],
            [arr[1], arr[2]]
        ]
        : [arr]
    ) as Array<[T, T]>
}

export default ContactInfo;
